import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import getDataFromApi from "../../../../../components/getDataFromApi";
import { UserPlusIcon, BackIcon, TenantIcon, PlusCircleIcon, CloseIcon } from "../../../../Common/SvgIcons/WorkflowIcons";
import Sidenav from "../../../../navbar/Sidenav";
import "./organizations.scss";
import { Button } from "reactstrap";

export default function CreateTenant({ setCreate, onSuccess }) {
    const navigate = useNavigate();

    const [schema, setSchema] = useState(null);
    const [formLoading, setFormLoading] = useState(true);
    const [form, setForm] = useState({});
    const [errors, setErrors] = useState({});
    const [fetchError, setFetchError] = useState(null);
    const [submitError, setSubmitError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const normalizeKey = (key) =>
        key.replace(/\s+/g, "_").toLowerCase();

    /* =========================
       FETCH SCHEMA ON LOAD
    ========================== */
    useEffect(() => {
        getDataFromApi(
            `${window.ENV.API_URL}/forms/admin/create_tenant`,
            "get"
        )
            .then((result) => {
                setSchema(result.json_schema);
                setFormLoading(false);
            })
            .catch((e) => {
                setFetchError(e);
                setFormLoading(false);
            });
    }, []);

    /* =========================
       INIT FORM FROM SCHEMA
    ========================== */
    useEffect(() => {
        if (!schema?.properties) return;

        const f = {};
        const e = {};

        Object.entries(schema.properties).forEach(([label, field]) => {
            const key = normalizeKey(label);

            if (field.enum) {
                if (label === "Plan") {
                    f[key] = field.enum[field.enum.length - 1];
                } else if (field.default) {
                    f[key] = field.default;
                } else {
                    f[key] = field.enum[0];
                }
            } else {
                f[key] = field.default || "";
            }

            e[key] = false;
        });

        setForm(f);
        setErrors(e);
    }, [schema]);

    const isRequired = (label) =>
        schema?.required?.includes(label);

    const isValidEmail = (email) => {
        const emailRegex =
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const isValidSlug = (slug) => {
        // Only lowercase letters and numbers, no spaces, no special chars

        const slugRegex = /^[a-z0-9]+$/;

        // If you want to allow hyphens (-) instead, use this:
        // const slugRegex = /^[a-z0-9-]+$/;
        return slugRegex.test(slug);
    };

    const handleChange = (key, value, required) => {
        const trimmed = value;

        setForm((prev) => ({ ...prev, [key]: trimmed }));

        if (required) {
            setErrors((prev) => ({
                ...prev,
                [key]: !trimmed
            }));
        }
    };

    /* =========================
       SUBMIT
    ========================== */
    const handleSubmit = async () => {
        const newErrors = {};
        setSubmitError(null);

        Object.keys(schema.properties).forEach((label) => {
            const key = normalizeKey(label);
            const value = form[key]?.toString().trim();

            if (isRequired(label) && !value) {
                newErrors[key] = true;
                return;
            }

            if (label === "Admin Email" && value && !isValidEmail(value)) {
                newErrors[key] = true;
            }

            if (label === "Tenant Slug" && value && !isValidSlug(value)) {
                newErrors[key] = true;
            }
        });

        setErrors((prev) => ({ ...prev, ...newErrors }));
        if (Object.keys(newErrors).length > 0) return;

        const payload = {};
        Object.keys(schema.properties).forEach((label) => {
            payload[label] = form[normalizeKey(label)];
        });

        try {
            setSubmitting(true);

            await getDataFromApi(
                `${window.ENV.API_URL}/forms/admin/create_tenant`,
                "post",
                payload
            );

            onSuccess?.();
            setCreate(false);
        } catch (e) {
            console.log("submit error", e);

            let message = "Failed to create tenant. Please try again.";

            const detail =
                e?.response?.data?.detail ||
                e?.body?.detail ||
                e?.detail;

            if (detail) {
                try {
                    // Extract JSON inside the string
                    const jsonPart = detail.substring(detail.indexOf("{"));
                    const parsed = JSON.parse(jsonPart);

                    message = parsed?.detail || message;

                    // Remove leading "409:" if present
                    message = message.replace(/^409:\s*/, "");
                } catch {
                    message = detail;
                }
            }

            setSubmitError(message);
        } finally {
        setSubmitting(false);
    }
};


if (!formLoading && fetchError) {
    const status =
        fetchError?.status ||
        fetchError?.response?.status ||
        fetchError?.response?.data?.status ||
        500;

    return (
        <div className="de-modal__backdrop">
            <div className="ms-auto de-modal d-flex flex-column justify-content-center align-items-center p-4">

                <div
                    style={{
                        backgroundColor: "#fff8d5",
                        border: "1px solid #ffe7a3",
                        borderRadius: "4px",
                        padding: "14px 24px",
                        maxWidth: "500px",
                        textAlign: "center",
                        fontSize: "0.95rem",
                        color: "black",
                    }}
                >
                    {status === 404
                        ? "Form configuration not found (404)."
                        : status === 403
                            ? "You don’t have permission to create tenants."
                            : status === 500
                                ? "Server error (500). Please try again."
                                : "Unable to load form right now."}
                </div>

                <button
                    className="de-modal__submit mt-3"
                    onClick={() => setCreate(false)}
                >
                    Close
                </button>
            </div>
        </div>
    );
}

/* =========================
   RENDER
========================== */
return (
    <>

        <div className="de-modal__backdrop">

            <div className="ms-auto h-100 w-100 d-flex justify-content-end"
            //  onClick={(e) => { e.preventDefault(); setCreate(false) }}
             >

                <div className="pb-3 de-modal create-tenant-page d-flex flex-column gap-2 evaluate-model-ui" onClick={(e) => { e.stopPropagation() }}>

                    {/* HEADER */}
                    <div className="d-flex align-items-start justify-content-between p-3" style={{ borderBottom: '1px solid var(--glass-color)' }}>
                        <div className="d-flex gap-2 align-tems-start w-100">
                            <div className="position-relative">
                                <TenantIcon className="wf-icon-blue text-white bg-primary fw-bold p-2" />

                                <PlusCircleIcon className="wf-icon-blue p-0" style={{ width: '16px', height: '16px', position: 'absolute', borderRadius: '50%', top: '7px', right: '5px' }} />
                            </div>
                            <div className="em-header flex-fill">
                                <div className="d-flex justify-content-between w-100">
                                    <h2 className="main-header mb-0">Create Tenant</h2>
                                    <button
                                        className="plain-button2 p-0"
                                        onClick={(e) => { e.preventDefault(); setCreate(false) }}
                                    >
                                        <CloseIcon className="wf-icon text-secondary" />
                                    </button>
                                </div>
                                <p className="sub-title mb-0">
                                    Configure a new tenant and assign a plan
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* <h2>ESTABLISH UNIT</h2>
                        <p className="sub-gray-text">
                            PROVISIONING CORPORATE ORGANIZATION
                        </p> */}

                    <div className="flex-fill px-4 overflow-auto">

                        {formLoading && <div>Loading form…</div>}

                        {!formLoading &&
                            schema &&
                            Object.entries(schema.properties).map(
                                ([label, field]) => {
                                    const key = normalizeKey(label);
                                    const required = isRequired(label);

                                    return (
                                        <div key={label} className="form-group">
                                            <div className={`${errors[key] ? "error" : ""} d-block fw-bold mt-3`} style={{ color: errors[key] && 'red'  }}>
                                                {field.title || label}</div>

                                            {field.enum ? (
                                                label === "Plan" ? (
                                                    // 🔽 PLAN AS DROPDOWN
                                                    <div className="de-modal__select">
                                                        <select
                                                            value={form[key]}
                                                            onChange={(e) =>
                                                                handleChange(
                                                                    key,
                                                                    e.target.value,
                                                                    required
                                                                )
                                                            }
                                                        >
                                                            {field.enum.map((val, idx) => (
                                                                <option key={val} value={val}>
                                                                    {field.enumNames?.[idx] || val}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                ) : (
                                                    // 🔘 TENANT STATUS BUTTON GROUP
                                                    <div className="de-modal__status">
                                                        {field.enum.map((val, idx) => (
                                                            <button
                                                                key={val}
                                                                type="button"
                                                                className={
                                                                    form[key] === val ? "active" : ""
                                                                }
                                                                onClick={() =>
                                                                    handleChange(
                                                                        key,
                                                                        val,
                                                                        required
                                                                    )
                                                                }
                                                            >
                                                                {field.enumNames?.[idx] || val}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )
                                            ) : (
                                                <>
                                                    {/* 📝 TEXT INPUTS WITH PLACEHOLDERS */}
                                                    <input
                                                        type={label === "Admin Email" ? "email" : "text"}
                                                        placeholder={`Enter ${field.title || label}`}
                                                        value={form[key]}
                                                        onChange={(e) =>
                                                            handleChange(
                                                                key,
                                                                e.target.value,
                                                                required
                                                            )
                                                        }
                                                    />

                                                    {/* Email Error Message */}
                                                    {label === "Admin Email" && errors[key] && (
                                                        <small className="text-secondary">
                                                            Please enter a valid email address
                                                        </small>
                                                    )}

                                                    {label === "Tenant Slug" && errors[key] && (
                                                        <small className="text-secondary">
                                                            Slug must contain only lowercase letters and numbers (no spaces or special characters)
                                                        </small>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    );
                                }
                            )}

                    </div>



                    {submitError && (
                        <div
                            style={{
                                backgroundColor: "#fff8d5",
                                border: "1px solid #ffe7a3",
                                borderRadius: "4px",
                                padding: "10px 16px",
                                fontSize: "0.9rem",
                                color: "black",
                                marginBottom: "12px"
                            }}
                        >
                            {typeof submitError === "string"
                                ? submitError
                                : submitError?.message || "Failed to create tenant. Please try again."}
                        </div>
                    )}

                    <div className="px-3 d-a justify-content-between pt-3" style={{ borderTop: '0.5px solid var(--glass-color)' }}>
                        <span></span>
                        <Button
                            color="primary"
                            className=" m-0 "
                            onClick={handleSubmit}
                            disabled={submitting}
                        >
                            {submitting ? "Creating..." : "Create Tenant"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>

        <div className="mobile-view-bottom"></div>

    </>
);
}